import Color from 'color';
import { lFColor0 } from 'pages/components/LFHeader/LFHeader.styled';
import { redColor } from 'pages/Logs/components/LogTable.styled';
import {
  activityLogsPanelMobileHeight,
  LEFT_SIDE_PANEL_MOTION_MS,
  leftSidePanelMotionEase,
  logRes1,
  mobileRes2,
  topBarColor2,
} from 'pages/styles';
import styled, { keyframes } from 'styled-components';
import { ACTIVITY_REPORT_ROW_HEIGHT_PX } from './reportActivityLogs';

const ink = '#1c1c1c';
/** Secondary labels / hints / chrome — kept darker than default gray-500. */
const inkMuted = '#3d4450';
const inkBody = '#2a3038';
const line = '#b8bec8';
const panelBg = '#fafafa';
const rowHover = Color(lFColor0).alpha(0.06).toString();
/** Highlighted activity chips / picker rows — blue, distinct from purple UI chrome. */
const highlightBlue = '#2563eb';
const highlightBlueSoft = Color(highlightBlue).alpha(0.08).toString();
const highlightBlueMid = Color(highlightBlue).alpha(0.12).toString();
const highlightBlueBorder = Color(highlightBlue).alpha(0.4).toString();
/** Warning yellow for recipient log-access note (four shades). */
const warnYellowFill = '#fff8e6';
const warnYellowBorder = '#e0b84a';
const warnYellowInk = '#8a6d1b';
const warnYellowIcon = '#c99712';

export const PageShell = styled.div`
  width: 100%;
  display: flex;
  height: 100%;
  z-index: 1;
  overflow: hidden;
  background: white;
  @media(max-width: ${logRes1}) {
    flex-direction: column;
  }
`;

export const PageBody = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* Report overlays (view original / full preview) stay in this pane. */
  position: relative;
`;

/** Inbox / peek fetch — same in-page slot pattern as /logs. */
export const LoaderSlot = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  position: relative;
`;

export const ReportingPanelMobile = styled.div`
  display: none;
  width: 100%;
  height: ${activityLogsPanelMobileHeight};
  box-shadow: 0px -1px 2px 2px rgba(0,0,0,0.17);
  -webkit-box-shadow: 0px -1px 2px 2px rgba(0,0,0,0.17);
  -moz-box-shadow: 0px -1px 2px 2px rgba(0,0,0,0.17);
  position: sticky;
  top: 0;
  z-index: 15;
  background: white;
  padding: 13px 19px;
  padding-right: 16px;
  box-sizing: border-box;
  @media(max-width: ${logRes1}) {
    display: flex;
    align-items: flex-end;
    font-weight: 500;
  }
  @media(max-width: ${mobileRes2}) {
    box-shadow: 0px -1px 2px 2px rgba(0,0,0,0.37);
    padding: 13px 14px;
    padding-right: 13px;
  }
`;

export const ReportingPanelMobileTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: ${topBarColor2};
  transform: translateY(1.5px);
`;

export const ReportingPanelMobileBtn = styled.button`
  margin-left: auto;
  padding: 10.5px 16px;
  font-size: 12.5px;
  width: max-content;
  border: none;
  border-radius: 5px;
  background: ${Color(topBarColor2).lighten(2.5).toString()};
  color: ${Color(topBarColor2).lighten(0.9).toString()};
  font-weight: 500;
  font-family: inherit;
  transform: translateY(-1px);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  & svg { font-size: 16px; }
  &:hover {
    background: ${Color(topBarColor2).lighten(2.2).toString()};
    color: ${Color(topBarColor2).lighten(0.2).toString()};
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  z-index: 1;
  overflow: hidden;
  background: white;
  position: relative;
`;

export const PageHeader = styled.div`
  background: white;
  color: #212529;
  padding: 0 15px;
  min-height: 60px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  border-bottom: 2px solid #e9ecef;
  position: relative;
  z-index: 2;
`;

export const PageTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

export const PageTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #212529;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PageSubtitle = styled.div`
  font-size: 12px;
  color: #6c757d;
  letter-spacing: 0.2px;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Main = styled.div`
  flex: 1;
  min-width: 0;
  overflow: auto;
  background: white;
  padding: 22px 24px 40px;
  box-sizing: border-box;
  @media(max-width: ${mobileRes2}) {
    padding: 16px 14px 32px;
  }
`;

/** Form + preview cluster, left-aligned in the pane. */
export const MainInner = styled.div`
  width: 100%;
  max-width: 1080px;
  box-sizing: border-box;
`;

/** Form left + live report preview right. */
export const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 28px;
  width: 100%;
  @media(max-width: 900px) {
    flex-direction: column;
    max-width: 640px;
    gap: 20px;
  }
`;

/** Form column. */
export const FormColumn = styled.div<{ $mobileHidden?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  max-width: 560px;
  @media(max-width: 900px) {
    max-width: none;
    width: 100%;
    display: ${({ $mobileHidden }) => ($mobileHidden ? 'none' : 'block')};
  }
`;

/** Sticky PDF-style preview + prepare actions — capped to the viewport. */
export const PreviewColumn = styled.div<{ $mobileHidden?: boolean }>`
  flex: 0 0 360px;
  width: 360px;
  position: sticky;
  top: 8px;
  align-self: flex-start;
  max-height: calc(100vh - 88px);
  max-height: calc(100dvh - 88px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
  @media(max-width: 900px) {
    flex: none;
    width: 100%;
    position: static;
    max-height: calc(100vh - 88px);
    max-height: calc(100dvh - 88px);
    display: ${({ $mobileHidden }) => ($mobileHidden ? 'none' : 'flex')};
  }
`;

export const MobileSectionTabs = styled.div`
  display: none;
  @media(max-width: 900px) {
    display: flex;
    gap: 6px;
    margin: 0 0 16px;
    width: 100%;
    max-width: 640px;
  }
`;

export const MobileSectionTab = styled.button<{ $active?: boolean }>`
  flex: 1;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? lFColor0 : '#f3f4f6')};
  color: ${({ $active }) => ($active ? '#fff' : '#2a3038')};
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  & svg { font-size: 16px; }
`;

export const ReplyBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${Color(lFColor0).alpha(0.35).toString()};
  background: ${Color(lFColor0).alpha(0.08).toString()};
`;

export const ReplyBannerText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${inkBody};
  line-height: 1.4;
`;

export const ReplyBannerActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

export const ReplyBannerBtn = styled.button<{ $primary?: boolean }>`
  height: 32px;
  padding: 0 12px;
  border-radius: 7px;
  border: 1px solid ${({ $primary }) => ($primary ? 'transparent' : line)};
  background: ${({ $primary }) => ($primary ? lFColor0 : 'white')};
  color: ${({ $primary }) => ($primary ? 'white' : inkBody)};
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  & svg { font-size: 16px; }
`;

export const PreviewActions = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** Recap of Send to / Purpose after the first new-report step. */
export const ComposerPhaseRecap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${line};
  background: ${panelBg};
`;

const recapIdentityIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

/** Recipient face + name on the body-step recap (same enlarge as /logs). */
export const ComposerPhaseRecapIdentity = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${recapIdentityIn} ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase};
`;

export const ComposerPhaseRecapText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${inkBody};
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PhaseContinueRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 4px;
  margin-bottom: 8px;
`;

export const Field = styled.div`
  margin-bottom: 18px;
`;

export const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${inkBody};
  margin-bottom: 8px;
`;

export const Required = styled.span`
  color: #c62828;
  margin-left: 4px;
`;

export const TextInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  max-width: 560px;
  height: 42px;
  border: 1px solid ${({ $error }) => ($error ? '#c62828' : line)};
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: ${ink};
  font-family: inherit;
  box-sizing: border-box;
  background: white;
  &:focus {
    outline: none;
    border-color: ${({ $error }) => (
    $error ? '#c62828' : Color(lFColor0).alpha(0.55).toString()
  )};
  }
  &::placeholder {
    color: ${inkMuted};
  }
  &:disabled {
    background: #f3f4f6;
    color: #4b5563;
    cursor: not-allowed;
  }
`;

export const TextArea = styled.textarea<{ $error?: boolean }>`
  width: 100%;
  max-width: 560px;
  min-height: 110px;
  border: 1px solid ${({ $error }) => ($error ? '#c62828' : line)};
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  color: ${ink};
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${({ $error }) => (
    $error ? '#c62828' : Color(lFColor0).alpha(0.55).toString()
  )};
  }
`;

/** Interleaved text + log-chip description composer. */
export const DescEditor = styled.div<{ $error?: boolean }>`
  width: 100%;
  border: 1px solid ${({ $error }) => ($error ? '#c62828' : line)};
  border-radius: 8px;
  padding: 8px;
  box-sizing: border-box;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 6px;
  &:focus-within {
    border-color: ${({ $error }) => (
    $error ? '#c62828' : Color(lFColor0).alpha(0.55).toString()
  )};
  }
`;

export const DescTextArea = styled.textarea`
  width: 100%;
  min-height: 40px;
  border: none;
  resize: none;
  overflow: hidden;
  padding: 8px 8px;
  font-size: 14px;
  line-height: 1.45;
  color: ${ink};
  font-family: inherit;
  box-sizing: border-box;
  background: transparent;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: #6b7280;
  }
`;

/** CC report body — email-style format bar (bold / italic / lists). */
export const DescRichToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 2px 4px;
  border-bottom: 1px solid ${line};
  margin-bottom: 2px;
`;

export const DescRichToolBtn = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => (
    $active ? Color(lFColor0).alpha(0.45).toString() : 'transparent'
  )};
  background: ${({ $active }) => (
    $active ? Color(lFColor0).alpha(0.12).toString() : 'transparent'
  )};
  color: ${ink};
  border-radius: 6px;
  min-width: 30px;
  height: 28px;
  padding: 0 8px;
  font-size: 12.5px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  line-height: 1;
  &:hover {
    background: ${Color(lFColor0).alpha(0.1).toString()};
  }
`;

export const DescRichEditable = styled.div`
  width: 100%;
  min-height: 40px;
  border: none;
  padding: 8px 8px;
  font-size: 14px;
  line-height: 1.45;
  color: ${ink};
  font-family: inherit;
  box-sizing: border-box;
  background: transparent;
  outline: none;
  word-break: break-word;
  white-space: pre-wrap;
  &:empty:before {
    content: attr(data-placeholder);
    color: #6b7280;
    pointer-events: none;
  }
  ul, ol {
    margin: 0.25em 0 0.25em 1.25em;
    padding: 0;
  }
  b, strong { font-weight: 700; }
  i, em { font-style: italic; }
  u { text-decoration: underline; }
`;

const descLogGroupIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** Consecutive activity s/n chips + optional total — matches preview/PDF sets. */
export const DescLogGroup = styled.div`
  margin: 2px 0;
  border: 1px solid ${highlightBlueBorder};
  border-radius: 8px;
  background: ${highlightBlueSoft};
  overflow: hidden;
  animation: ${descLogGroupIn} ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const DescLogGroupTotal = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 36px 8px 10px;
  border-top: 1px solid ${highlightBlueBorder};
  background: ${highlightBlueMid};
`;

export const DescLogGroupTotalTerm = styled.span`
  min-width: 0;
  flex: 1;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${highlightBlue};
`;

export const DescLogGroupTotalFigure = styled.span`
  flex-shrink: 0;
  box-sizing: border-box;
  width: 96px;
  padding-right: 10px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: ${ink};
`;

export const DescLogChip = styled.div<{ $grouped?: boolean }>`
  display: flex;
  align-items: stretch;
  border: ${({ $grouped }) => ($grouped ? 'none' : `1px solid ${highlightBlueBorder}`)};
  ${({ $grouped }) => $grouped && `
    border-bottom: 1px solid ${highlightBlueBorder};
    &:last-child {
      border-bottom: none;
    }
  `}
  border-radius: ${({ $grouped }) => ($grouped ? '0' : '8px')};
  background: ${highlightBlueSoft};
  overflow: hidden;
  &:focus {
    outline: 2px solid ${Color(highlightBlue).alpha(0.45).toString()};
    outline-offset: ${({ $grouped }) => ($grouped ? '-2px' : '1px')};
  }
`;

export const DescLogMain = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 8px 10px;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  &:hover {
    background: ${Color(highlightBlue).alpha(0.06).toString()};
  }
`;

export const DescLogId = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: ${highlightBlue};
  padding-top: 1px;
`;

export const DescLogMsg = styled.span`
  min-width: 0;
  flex: 1;
  font-size: 13px;
  line-height: 1.35;
  color: #4b5563;
  word-break: break-word;
`;

export const DescLogAmount = styled.span<{ $na?: boolean }>`
  flex-shrink: 0;
  align-self: center;
  box-sizing: border-box;
  width: 96px;
  padding-top: 1px;
  padding-right: 10px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $na }) => ($na ? inkMuted : ink)};
`;

export const DescLogRemove = styled.button`
  flex-shrink: 0;
  width: 36px;
  border: none;
  border-left: 1px solid ${Color(highlightBlue).alpha(0.35).toString()};
  background: transparent;
  color: ${inkMuted};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: ${highlightBlue};
    background: ${Color(highlightBlue).alpha(0.08).toString()};
  }
`;

export const ErrorText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #c62828;
  font-weight: 500;
`;

export const CharCount = styled.div<{ $nearLimit?: boolean }>`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ $nearLimit }) => ($nearLimit ? '#c62828' : inkMuted)};
  text-align: right;
  max-width: 560px;
`;

export const ToggleRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${ink};
  font-family: inherit;
`;

/** Space below in-context people before alert-level summary (Command Center compliance). */
export const ComplianceAlertSummarySection = styled.div`
  margin-top: 14px;
`;

/** Charts toggle with chart title stacked below in Reference activityLogs. */
export const ChartsToggleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

export const ChartsTitleField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  width: 100%;
  padding-left: 26px;
  box-sizing: border-box;
`;

export const ChartsTitleLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${inkMuted};
  line-height: 1.2;
`;

export const ChartsTitleInput = styled.input`
  width: 100%;
  max-width: 220px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: white;
  color: ${ink};
  font-size: 13px;
  font-family: inherit;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${Color(lFColor0).alpha(0.55).toString()};
  }
  &::placeholder {
    color: ${inkMuted};
  }
`;

/** White chip actions under description (Open activityLogs / Reference activityLogs). */
export const ActivityLogsActionBtn = styled.button`
  height: 40px;
  padding: 0 14px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: white;
  color: ${ink};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  width: max-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  svg {
    font-size: 18px !important;
    color: ${lFColor0};
  }
  &:hover {
    border-color: ${Color(lFColor0).alpha(0.45).toString()};
    color: ${lFColor0};
    background: ${rowHover};
  }
`;

/** @deprecated alias — same chip style */
export const ReferenceActivityLogsOpenBtn = ActivityLogsActionBtn;

export const ActivityLogsActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding-top: 12px;
`;

const openActivityLogsPageBtnIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.88);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

/** Icon next to Reference activityLogs — opens /logs so filters can be set first. */
export const OpenActivityLogsPageBtn = styled.a`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: white;
  color: ${inkMuted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  animation: ${openActivityLogsPageBtnIn} ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase};
  svg {
    font-size: 20px !important;
    color: ${lFColor0};
  }
  &:hover {
    border-color: ${Color(lFColor0).alpha(0.45).toString()};
    background: ${rowHover};
    color: ${lFColor0};
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Check = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${lFColor0};
  cursor: pointer;
`;

export const ActivityLogsAttach = styled.div`
  margin-top: 12px;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ActivityLogsAttachCopy = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #6b7280;

  strong {
    color: #4b5563;
    font-weight: 600;
  }
`;

export const ReferencedBlock = styled.div`
  margin-top: 12px;
  border: 1px solid ${line};
  border-radius: 10px;
  overflow: hidden;
  background: ${Color(lFColor0).alpha(0.04).toString()};
`;

/** Frozen activity-set summary above "Reference new activityLogs". */
export const ActivitySetsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
  max-width: 420px;
`;

export const ActivitySetsPager = styled.div`
  margin-top: 2px;
  padding: 2px 0 0;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export const ActivitySetBlock = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ $active }) => (
    $active ? highlightBlueBorder : line
  )};
  border-radius: 8px;
  background: ${({ $active }) => (
    $active ? highlightBlueSoft : 'white'
  )};
  overflow: hidden;
`;

/** Non-clickable summary — open via View button. */
export const ActivitySetBlockMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: ${ink};
`;

export const ActivitySetSerial = styled.span`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: white;
  background: ${lFColor0};
`;

export const ActivitySetMeta = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 13px;
  font-weight: 600;
  color: ${inkBody};
`;

export const ActivitySetCount = styled.span`
  font-weight: 500;
  font-size: 12px;
  color: #9aa0a8;
`;

/** Open the manage panel for this frozen set (references + charts). */
export const ActivitySetViewBtn = styled.button<{ $active?: boolean }>`
  flex-shrink: 0;
  align-self: center;
  height: 28px;
  /* Active set hides the edit control — keep space before the remove icon. */
  margin: 0 ${({ $active }) => ($active ? '6px' : '4px')} 0 0;
  padding: 0 10px;
  border: 1px solid ${line};
  border-radius: 6px;
  background: white;
  color: ${inkBody};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  &:hover {
    color: ${lFColor0};
    border-color: ${Color(lFColor0).alpha(0.45).toString()};
    background: ${rowHover};
  }
  svg {
    font-size: 15px !important;
  }
`;

/** Switch description editing to a previous activity set — circular icon control. */
export const ActivitySetEditBtn = styled.button`
  flex-shrink: 0;
  align-self: center;
  width: 28px;
  height: 28px;
  margin: 0 6px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${inkMuted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  &:hover {
    color: ${lFColor0};
    background: ${rowHover};
  }
  svg {
    font-size: 16px;
  }
`;

/** Same placement as edit — circular icon, not a full-height bordered column. */
export const ActivitySetRemove = styled.button`
  flex-shrink: 0;
  align-self: center;
  width: 28px;
  height: 28px;
  margin: 0 6px 0 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${inkMuted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  font-family: inherit;
  &:hover {
    color: #c62828;
    background: ${Color('#c62828').alpha(0.06).toString()};
  }
`;

/** One activity set block in the report preview (text + charts). */
export const PreviewActivitySection = styled.div`
  & + & {
    margin-top: 12px;
  }
`;

export const ReferencedTitle = styled.div`
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${lFColor0};
  border-bottom: 1px solid ${line};
  background: white;
`;

export const ReferencedRow = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid ${Color(line).lighten(0.02).toString()};
  background: white;
  &:last-child {
    border-bottom: none;
  }
`;

export const ReferencedMain = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: inherit;
  &:hover {
    background: ${rowHover};
  }
`;

export const ReferencedRemove = styled.button`
  flex-shrink: 0;
  width: 40px;
  border: none;
  border-left: 1px solid ${line};
  background: white;
  color: ${inkMuted};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: ${lFColor0};
    background: ${rowHover};
  }
`;

export const ReportPreviewCard = styled.div`
  border: 1px solid ${line};
  border-radius: 10px;
  background: ${panelBg};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
  box-shadow: 0 1px 2px ${Color('#000').alpha(0.04).toString()};
`;

export const ReportPreviewBody = styled.div`
  padding: 14px 14px 16px;
  background: white;
  overflow: auto;
  min-height: 0;
  flex: 1;
`;

export const PreviewSectionBlock = styled.div`
  & + & {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid ${line};
  }
`;

export const ReportSectionCard = styled.div<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => (
    $active ? Color(lFColor0).alpha(0.45).toString() : line
  )};
  border-radius: 10px;
  background: ${({ $active }) => (
    $active ? Color(lFColor0).alpha(0.03).toString() : panelBg
  )};
  padding: 10px 10px 8px;
  margin-top: 10px;
  &:first-of-type {
    margin-top: 0;
  }
`;

export const ReportSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
`;

export const ReportSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${inkMuted};
`;

export const ReportPreviewLabel = styled.div`
  margin-top: 12px;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${inkMuted};
  &:first-child {
    margin-top: 0;
  }
`;

export const ReportPreviewText = styled.div<{ $muted?: boolean }>`
  font-size: 13.5px;
  line-height: 1.5;
  color: ${({ $muted }) => ($muted ? inkMuted : ink)};
  white-space: pre-wrap;
  word-break: break-word;
  & + & {
    margin-top: 10px;
  }
  /* Email-style body from Command Center reports */
  ul, ol {
    margin: 0.35em 0 0.35em 1.25em;
    padding: 0;
    white-space: normal;
  }
  b, strong { font-weight: 700; }
  i, em { font-style: italic; }
  u { text-decoration: underline; }
`;

export const ReportPreviewSubject = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  color: ${ink};
  word-break: break-word;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${line};
`;

export const ReportPreviewSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${inkMuted};
  margin-bottom: 8px;
`;

export const ReportPreviewAlertSummary = styled.div`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${line};
  background: #fafafa;
`;

export const ReportPreviewAlertSummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${inkMuted};
  margin-bottom: 6px;
`;

export const ReportPreviewAlertSummaryText = styled.div`
  font-size: 13.5px;
  line-height: 1.5;
  color: ${ink};
  white-space: pre-wrap;
  word-break: break-word;
`;

export const ReportPreviewRef = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-bottom: 1px solid ${highlightBlueBorder};
  background: ${highlightBlueSoft};
  &:last-of-type {
    border-bottom: none;
  }
`;

/** Consecutive logs + one total footer (report body only — not the description editor). */
export const ReportPreviewLogGroup = styled.div`
  margin: 8px 0;
  border: 1px solid ${highlightBlueBorder};
  border-radius: 8px;
  background: ${highlightBlueSoft};
  overflow: hidden;
`;

export const ReportPreviewLogGroupTotal = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 10px;
  border-top: 1px solid ${highlightBlueBorder};
  background: ${highlightBlueMid};
`;

export const ReportPreviewLogGroupTotalTerm = styled.span`
  min-width: 0;
  flex: 1;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${highlightBlue};
`;

export const ReportPreviewLogGroupTotalFigure = styled.span`
  flex-shrink: 0;
  width: 96px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: ${ink};
`;

export const PreviewCard = styled.div`
  border: 1px solid ${line};
  border-radius: 10px;
  background: ${panelBg};
  overflow: hidden;
`;

export const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: white;
  border-bottom: 1px solid ${line};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${inkBody};
`;

export const PreviewHeaderTitle = styled.div`
  min-width: 0;
`;

/** Header clickable — not a filled button. */
export const PreviewHeaderAction = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  color: ${lFColor0};
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: 0.02em;
  text-transform: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  & svg { font-size: 16px; }
  &:hover {
    text-decoration: underline;
  }
`;

export const HighlightHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  font-size: 12.5px;
  line-height: 1.4;
  color: ${inkBody};
  background: ${Color(lFColor0).alpha(0.06).toString()};
  border-bottom: 1px solid ${line};
`;

export const HighlightHintStar = styled.span`
  flex-shrink: 0;
  color: ${lFColor0};
  font-weight: 700;
  line-height: 1.35;
`;

export const ActivityList = styled.div`
  background: white;
`;

export const ActivityPreviewRow = styled.button<{ $highlighted?: boolean, $focused?: boolean }>`
  display: flex;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  min-height: ${ACTIVITY_REPORT_ROW_HEIGHT_PX}px;
  padding: 8px 12px;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid ${Color(line).lighten(0.02).toString()};
  background: ${({ $highlighted, $focused }) => (
    $focused || $highlighted ? highlightBlueMid : 'white'
  )};
  box-shadow: ${({ $focused }) => (
    $focused ? `inset 3px 0 0 ${highlightBlue}` : 'none'
  )};
  transition: background 180ms ease, box-shadow 180ms ease;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: inherit;
  &:hover {
    background: ${({ $highlighted, $focused }) => (
    $highlighted || $focused
      ? Color(highlightBlue).alpha(0.16).toString()
      : rowHover
  )};
  }
  &:last-child {
    border-bottom: none;
  }
`;

export const ActivityTmpId = styled.span<{ $highlighted?: boolean }>`
  flex-shrink: 0;
  align-self: center;
  min-width: 28px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $highlighted }) => ($highlighted ? highlightBlue : inkMuted)};
`;

export const ActivityHighlightSlot = styled.span`
  flex-shrink: 0;
  align-self: center;
  width: 14px;
  font-size: 12px;
  color: ${highlightBlue};
  font-weight: 700;
  text-align: center;
`;

export const ReportPreviewLogDate = styled.div`
  font-size: 11px;
  line-height: 1.35;
  color: #6b7280;
  margin-bottom: 2px;
`;

export const ReportPreviewLogMain = styled.div`
  min-width: 0;
  flex: 1;
`;

/** Shared width with the group total figure so amounts (including N/A) line up. */
export const ReportPreviewLogValue = styled.div<{ $na?: boolean }>`
  flex-shrink: 0;
  box-sizing: border-box;
  width: 96px;
  padding-top: 1px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $na }) => ($na ? inkMuted : ink)};
`;

export const ActivityMsg = styled.div<{ $clamp?: boolean }>`
  min-width: 0;
  flex: 1;
  align-self: center;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.4;
  word-break: break-word;
  ${({ $clamp }) => $clamp && `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
  `}
`;

/** Pinned activity on activity reports — mirrors /logs table row layout. */
export const ActivityReportLogRow = styled.div<{ $compact?: boolean }>`
  display: grid;
  grid-template-columns: minmax(72px, 18%) minmax(0, 1fr) 96px;
  gap: 10px;
  align-items: stretch;
  padding: ${({ $compact }) => ($compact ? '10px 12px' : '12px 14px')};
  border: 1px solid ${line};
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
`;

export const ActivityReportLogType = styled.div`
  font-weight: 600;
  color: ${inkBody};
  word-break: break-word;
`;

export const ActivityReportLogMessage = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const DescPictureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px dashed ${line};
  border-radius: 8px;
  background: ${panelBg};
  font-size: 13px;
`;

export const DescPictureName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${inkBody};
`;

export const DescPictureRemove = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${inkMuted};
  display: flex;
  padding: 2px;
`;

export const DescPictureAddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

export const DescPictureAddBtn = styled.button`
  border: 1px solid ${line};
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12.5px;
  cursor: pointer;
  color: ${inkBody};
  font-family: inherit;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const DescPictureThumb = styled.img`
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid ${line};
  flex-shrink: 0;
`;

const pictureSourceFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/** Camera vs device file — same choice pattern as profile photo. */
export const PictureSourceOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 24, 36, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  animation: ${pictureSourceFadeIn} 140ms ease;
  padding: 16px;
`;

export const PictureSourceCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 22px 24px 20px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);
  position: relative;
`;

export const PictureSourceTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${ink};
  margin-bottom: 6px;
  padding-right: 28px;
`;

export const PictureSourceSubtitle = styled.div`
  font-size: 13px;
  color: ${inkMuted};
  line-height: 1.4;
  margin-bottom: 16px;
`;

export const PictureSourceOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const PictureSourceOption = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  border: 1.5px solid #e9ecef;
  background: white;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
  font-family: inherit;

  &:hover:not(:disabled) {
    background: ${Color(lFColor0).alpha(0.05).toString()};
    border-color: ${Color(lFColor0).alpha(0.35).toString()};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const PictureSourceOptionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 10px;
  background: ${Color(lFColor0).alpha(0.1).toString()};
  color: ${lFColor0};
`;

export const PictureSourceOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PictureSourceOptionLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${ink};
`;

export const PictureSourceOptionHint = styled.div`
  font-size: 12px;
  color: ${inkMuted};
  line-height: 1.35;
`;

export const ReportPreviewPicture = styled.div`
  margin: 8px 0;
`;

export const ReportPreviewPictureImg = styled.img`
  max-width: 100%;
  border-radius: 8px;
  border: 1px solid ${line};
  display: block;
`;

export const ReportPreviewPictureCaption = styled.div`
  font-size: 12px;
  color: ${inkMuted};
  margin-top: 4px;
`;


/** Right-aligned cash/units amount on Reference activityLogs rows (ActivityLogs table tone). */
export const ActivityValue = styled.span<{ $negative?: boolean, $na?: boolean }>`
  flex-shrink: 0;
  align-self: stretch;
  box-sizing: border-box;
  width: 136px;
  margin: -8px -12px -8px auto;
  padding: 8px 12px;
  border-left: 1px solid ${Color(line).lighten(0.22).toString()};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 12.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $na, $negative }) => {
    if ($na) return inkMuted;
    return $negative ? '#eb364b' : Color('#40b017').darken(0.25).toString();
  }};
`;

export const EmptyActivityLogs = styled.div`
  padding: 24px 14px;
  text-align: center;
  color: ${inkBody};
  font-size: 13px;
`;

export const DraftStatus = styled.div`
  font-size: 12.5px;
  color: ${inkBody};
  line-height: 1.4;
`;

/** Chart.js metrics carousel (one chart at a time). */
export const MetricsCarousel = styled.div`
  margin-top: 0;
`;

/** One activity set's metrics block in the stacked preview. */
export const MetricsSetBlock = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid ${line};
`;

export const MetricsCarouselHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const MetricsCarouselTitle = styled.div`
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: ${ink};
  line-height: 1.3;
`;

/** Chart kind (Balance by SKU) — secondary to the set label above. */
export const MetricsCarouselChartName = styled.div`
  margin-top: 3px;
  font-size: 12px;
  font-weight: 500;
  color: ${lFColor0};
  line-height: 1.35;
`;

export const MetricsCarouselMeta = styled.div`
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  color: ${inkMuted};
`;

export const MetricsNavBtn = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: white;
  color: ${inkBody};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover:not(:disabled) {
    border-color: ${Color(lFColor0).alpha(0.45).toString()};
    color: ${lFColor0};
    background: ${rowHover};
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

export const MetricsChartWrap = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  width: 100%;
  position: relative;
`;

/** Center total inside a doughnut hole. */
export const MetricsPieCenter = styled.div`
  position: absolute;
  left: 50%;
  top: 42%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  max-width: 42%;
  z-index: 1;
`;

export const MetricsPieCenterValue = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${ink};
  line-height: 1.15;
  letter-spacing: -0.02em;
  word-break: break-word;
`;

export const MetricsPieCenterLabel = styled.div`
  margin-top: 2px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${inkMuted};
`;

/** Explains why charts / balances are missing or limited. */
export const MetricsStatusNote = styled.div<{ $tone?: 'info' | 'warn' }>`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $tone }) => (
    $tone === 'warn'
      ? Color('#b45309').alpha(0.35).toString()
      : Color(highlightBlue).alpha(0.35).toString()
  )};
  background: ${({ $tone }) => (
    $tone === 'warn'
      ? Color('#b45309').alpha(0.07).toString()
      : Color(highlightBlue).alpha(0.06).toString()
  )};
  font-size: 12px;
  line-height: 1.45;
  color: ${inkBody};
`;

export const MetricsStatusTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${inkMuted};
  margin-bottom: 4px;
`;

export const PreviewLogsSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${line};
`;

export const PreviewLogsCount = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: ${inkBody};
`;

export const SecondaryBtn = styled.button`
  height: 40px;
  padding: 0 16px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: white;
  color: ${ink};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    border-color: ${Color(lFColor0).alpha(0.45).toString()};
    color: ${lFColor0};
    background: ${rowHover};
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

export const PagesCont = styled.div`
  margin-top: 0;
  padding: 10px 12px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-top: 1px solid ${line};
`;

export const PagesCont1 = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: max-content;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

export const SendBtn = styled.button`
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: ${lFColor0};
  color: white;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover {
    background: ${Color(lFColor0).darken(0.08).toString()};
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

export const GhostBtn = styled.button`
  height: 40px;
  padding: 0 14px;
  border: 1px solid ${line};
  border-radius: 8px;
  background: white;
  color: ${ink};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  width: max-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover {
    border-color: ${Color(lFColor0).alpha(0.45).toString()};
    color: ${lFColor0};
    background: ${rowHover};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const AddSectionBtn = styled(GhostBtn)`
  margin-top: 10px;
`;

export const RecipientHint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${inkBody};
`;

const recipientAccessNoteIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const recipientAccessNoteOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-6px);
  }
`;

/** Lower-access recipient will see logs they could not open before. */
export const RecipientAccessNote = styled.div<{ $exiting?: boolean }>`
  margin-top: 10px;
  margin-bottom: 2px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${warnYellowBorder};
  background: ${warnYellowFill};
  font-size: 12.5px;
  font-weight: 500;
  color: ${warnYellowInk};
  line-height: 1.45;
  max-width: 420px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  animation: ${({ $exiting }) => (
    $exiting ? recipientAccessNoteOut : recipientAccessNoteIn
  )} ${LEFT_SIDE_PANEL_MOTION_MS}ms ${leftSidePanelMotionEase} both;
  svg {
    flex-shrink: 0;
    margin-top: 1px;
    font-size: 18px !important;
    color: ${warnYellowIcon};
  }
`;

export const FieldHint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${inkMuted};
  line-height: 1.45;
  max-width: 560px;
`;

const removeActivitySetConfirmIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** Confirm before dropping a frozen activity set from the composer. */
export const RemoveActivitySetConfirmCard = styled.div`
  position: relative;
  width: min(92vw, 400px);
  padding: 22px 22px 18px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 12px 40px ${Color(ink).alpha(0.14).toString()};
  animation: ${removeActivitySetConfirmIn} 220ms ${leftSidePanelMotionEase};
`;

export const RemoveActivitySetConfirmHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-right: 28px;
  margin-bottom: 16px;
`;

export const RemoveActivitySetConfirmIcon = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Color(lFColor0).alpha(0.08).toString()};
  color: ${lFColor0};

  & > svg {
    font-size: 22px;
  }
`;

export const RemoveActivitySetConfirmCopy = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const RemoveActivitySetConfirmTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: ${ink};
`;

export const RemoveActivitySetConfirmBody = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  color: ${inkMuted};
`;

export const RemoveActivitySetConfirmPreview = styled.div`
  margin-bottom: 18px;
`;

export const RemoveActivitySetConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

export const RemoveActivitySetConfirmCancel = styled.button`
  appearance: none;
  border: 1px solid ${line};
  background: white;
  color: ${inkBody};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  & svg { font-size: 16px; }
  transition: background 160ms ease, border-color 160ms ease, color 160ms ease;

  &:hover {
    background: ${panelBg};
    border-color: ${Color(line).darken(0.08).toString()};
    color: ${ink};
  }
`;

export const RemoveActivitySetConfirmSubmit = styled.button`
  appearance: none;
  border: none;
  background: ${redColor};
  color: white;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 160ms ease, transform 160ms ${leftSidePanelMotionEase};

  & > svg {
    font-size: 17px;
  }

  &:hover {
    background: ${Color(redColor).darken(0.08).toString()};
  }

  &:active {
    transform: scale(0.98);
  }
`;

